/**
 * Drizzle ORM schema for billing tables.
 *
 * FK constraints to organizations are defined only in the SQL migration
 * to avoid cross-package schema imports.
 */

import { sql } from 'drizzle-orm';
import { sqliteTable, text, integer, index, uniqueIndex } from 'drizzle-orm/sqlite-core';
import type { PlanLimits, InvoiceStatus } from './billing-types';

// ========================================
// SUBSCRIPTION PLANS
// ========================================

export const subscriptionPlans = sqliteTable('subscription_plans', {
	id: text('id').primaryKey(),
	tier: text('tier').notNull(), // 'free' | 'pro' | 'team' | 'enterprise'
	name: text('name').notNull(),
	stripeProductId: text('stripe_product_id'),
	stripePriceId: text('stripe_price_id'),
	priceCents: integer('price_cents'), // 0 for free, null for enterprise
	isPerSeat: integer('is_per_seat', { mode: 'boolean' }).notNull().default(false),
	limits: text('limits', { mode: 'json' }).notNull().$type<PlanLimits>(),
	isContactSales: integer('is_contact_sales', { mode: 'boolean' }).notNull().default(false),
	isActive: integer('is_active', { mode: 'boolean' }).notNull().default(true),
	createdAt: integer('created_at').notNull().default(sql`(unixepoch())`),
	updatedAt: integer('updated_at').notNull().default(sql`(unixepoch())`),
}, (table) => ({
	tierIdx: index('subscription_plans_tier_idx').on(table.tier),
}));

// ========================================
// ORGANIZATION SUBSCRIPTIONS
// ========================================

export const orgSubscriptions = sqliteTable('org_subscriptions', {
	id: text('id').primaryKey(),
	orgId: text('org_id').notNull(), // FK defined in SQL migration
	planId: text('plan_id').notNull(), // FK defined in SQL migration
	stripeCustomerId: text('stripe_customer_id'),
	stripeSubscriptionId: text('stripe_subscription_id'),
	status: text('status').notNull().default('active'), // 'active' | 'past_due' | 'canceled' | 'trialing' | 'incomplete'
	currentPeriodStart: integer('current_period_start'),
	currentPeriodEnd: integer('current_period_end'),
	cancelAtPeriodEnd: integer('cancel_at_period_end', { mode: 'boolean' }).notNull().default(false),
	quantity: integer('quantity').notNull().default(1),
	createdAt: integer('created_at').notNull().default(sql`(unixepoch())`),
	updatedAt: integer('updated_at').notNull().default(sql`(unixepoch())`),
}, (table) => ({
	orgIdIdx: uniqueIndex('org_subscriptions_org_id_idx').on(table.orgId),
	stripeSubIdx: uniqueIndex('org_subscriptions_stripe_sub_idx').on(table.stripeSubscriptionId),
	stripeCustomerIdx: index('org_subscriptions_stripe_customer_idx').on(table.stripeCustomerId),
}));

// ========================================
// USAGE RECORDS
// ========================================

export const usageRecords = sqliteTable('usage_records', {
	id: text('id').primaryKey(),
	orgId: text('org_id').notNull(), // FK to organizations not enforced in SQLite; validated at application layer
	metric: text('metric').notNull(), // 'builds' | 'iterations' | 'hosted_apps'
	quantity: integer('quantity').notNull().default(1),
	appId: text('app_id'),
	userId: text('user_id'),
	billingPeriodStart: integer('billing_period_start').notNull(),
	recordedAt: integer('recorded_at').notNull().default(sql`(unixepoch())`),
	stripeUsageRecordId: text('stripe_usage_record_id'),
}, (table) => ({
	orgMetricPeriodIdx: index('usage_records_org_metric_period_idx').on(
		table.orgId, table.metric, table.billingPeriodStart
	),
	appIdIdx: index('usage_records_app_id_idx').on(table.appId),
}));

// ========================================
// INVOICES
// ========================================

export const invoices = sqliteTable('invoices', {
	id: text('id').primaryKey(),
	orgId: text('org_id').notNull(), // FK to organizations not enforced in SQLite; validated at application layer
	stripeInvoiceId: text('stripe_invoice_id').notNull(),
	amountCents: integer('amount_cents').notNull(),
	currency: text('currency').notNull().default('usd'),
	status: text('status').notNull().$type<InvoiceStatus>(),
	subscriptionAmountCents: integer('subscription_amount_cents'),
	overageAmountCents: integer('overage_amount_cents'),
	periodStart: integer('period_start'),
	periodEnd: integer('period_end'),
	invoiceUrl: text('invoice_url'),
	createdAt: integer('created_at').notNull().default(sql`(unixepoch())`),
	updatedAt: integer('updated_at').notNull().default(sql`(unixepoch())`),
}, (table) => ({
	orgIdIdx: index('invoices_org_id_idx').on(table.orgId),
	stripeInvoiceIdx: uniqueIndex('invoices_stripe_invoice_idx').on(table.stripeInvoiceId),
}));
